/**
 * Sector Operations — Clinics Narcotics Vault, Move Ledger & Two-Person Count.
 * R39 M9.a (plan docs/plans/sector-operations.md §2 SO-P267, SO-D230, SO-P213).
 */

export const OPS_CLI_NARC_SCHEDULES = ['H1', 'X', 'NDPS'] as const
export type OpsCliNarcSchedule = (typeof OPS_CLI_NARC_SCHEDULES)[number]

export const OPS_CLI_NARC_UNITS = ['AMPOULE', 'VIAL', 'TABLET', 'ML'] as const
export type OpsCliNarcUnit = (typeof OPS_CLI_NARC_UNITS)[number]

export const OPS_CLI_NARC_MOVE_KINDS = [
  'RECEIPT',
  'ISSUE',
  'USE',
  'WASTAGE',
  'RETURN',
  'ADJUST',
] as const
export type OpsCliNarcMoveKind = (typeof OPS_CLI_NARC_MOVE_KINDS)[number]

export const OPS_CLI_NARC_COUNT_STATES = [
  'OPEN',
  'AGREED',
  'DISPUTED',
  'MISMATCH',
] as const
export type OpsCliNarcCountState = (typeof OPS_CLI_NARC_COUNT_STATES)[number]

export interface OpsCliNarcItem {
  id: string
  workspaceId: string
  vaultAssetId: string
  placeId: string
  name: string
  schedule: OpsCliNarcSchedule
  unit: OpsCliNarcUnit
  balance: number
  archived: boolean
  createdAt?: number
  updatedAt?: number
}

export interface OpsCliNarcMove {
  id: string
  workspaceId: string
  itemId: string
  vaultAssetId: string
  kind: OpsCliNarcMoveKind
  qty: number
  balanceAfter: number
  byUid: string
  witnessUid?: string
  rxPhotoPath?: string
  note?: string
  opId?: string
  createdAt: number
}

export interface OpsCliNarcCount {
  id: string
  workspaceId: string
  vaultAssetId: string
  counterUid: string
  witnessUid: string
  counterTally?: Record<string, number>
  witnessTally?: Record<string, number>
  expected: Record<string, number>
  state: OpsCliNarcCountState
  requestId?: string
  createdAt: number
  completedAt?: number
}

export interface CreateNarcItemInput {
  workspaceId: string
  vaultAssetId: string
  name: string
  schedule: OpsCliNarcSchedule
  unit: OpsCliNarcUnit
  initialBalance?: number
  opId?: string
}

export interface ArchiveNarcItemInput {
  workspaceId: string
  itemId: string
  archived?: boolean
}

export interface RecordNarcMoveInput {
  workspaceId: string
  itemId: string
  vaultAssetId: string
  kind: OpsCliNarcMoveKind
  qty: number
  moveId?: string
  witnessUid?: string
  rxPhotoPath?: string
  note?: string
  opId?: string
  adjustDirection?: 'INCREASE' | 'DECREASE' | 'IN' | 'OUT'
  adjustSign?: '+' | '-'
}

export interface StartNarcCountInput {
  workspaceId: string
  vaultAssetId: string
  witnessUid: string
}

export interface SubmitNarcTallyInput {
  workspaceId: string
  countId: string
  tally: Record<string, number>
}

export interface GetRxPhotoUrlInput {
  workspaceId: string
  moveId: string
}

export interface ExportNarcRegisterInput {
  workspaceId: string
  vaultAssetId: string
  month: string // YYYY-MM
}

export function isValidNarcSchedule(schedule: unknown): schedule is OpsCliNarcSchedule {
  return typeof schedule === 'string' && (OPS_CLI_NARC_SCHEDULES as readonly string[]).includes(schedule)
}

export function isValidNarcUnit(unit: unknown): unit is OpsCliNarcUnit {
  return typeof unit === 'string' && (OPS_CLI_NARC_UNITS as readonly string[]).includes(unit)
}

export function isValidNarcMoveKind(kind: unknown): kind is OpsCliNarcMoveKind {
  return typeof kind === 'string' && (OPS_CLI_NARC_MOVE_KINDS as readonly string[]).includes(kind)
}

export function isValidNarcCountState(state: unknown): state is OpsCliNarcCountState {
  return typeof state === 'string' && (OPS_CLI_NARC_COUNT_STATES as readonly string[]).includes(state)
}

export function validateNarcRxPhotoPath(
  workspaceId: string,
  moveId: string,
  uid: string,
  photoPath: string,
): { valid: boolean; error?: string } {
  const expectedPrefix = `workspaces/${workspaceId}/ops_cli_rx/${moveId}/${uid}/`
  if (!photoPath.startsWith(expectedPrefix)) {
    return { valid: false, error: `Invalid Rx photo path prefix. Must start with ${expectedPrefix}` }
  }
  const fileName = photoPath.slice(expectedPrefix.length)
  if (!/^[A-Za-z0-9_.-]+$/.test(fileName)) {
    return { valid: false, error: 'Invalid Rx photo filename.' }
  }
  return { valid: true }
}
